import { useCallback, useEffect, useId } from 'react';

import { useVerticalProductTeaserContainer } from '../../../ecommerce/hooks/useProductTeaserContainer';
import { productFlow } from '../../flow/product/productFlow';
import { useAsyncDispatcher } from '../../hooks/useAsyncDispatcher';
import { useAppSelector } from '../../hooks/useThunkDispatch';
import type { ProductProps } from '../../models/props/itemTeaserProps';
import { storeSelectors } from '../../selectors/storeSelectors';
import ListBlock from '../ListBlock/ListBlock';
import { BlockHeader } from '../ListBlock/ListBlockHeader';
import BuyableProductTeaser from '../molecules/BuyableProductTeaser';

interface SamePromotionProductListProps {
    product: ProductProps;
    onProductsLoaded: (promoProductsCount: number) => void;
}

const listHeading = 'Varor som ingår i erbjudandet';

const SamePromotionProductList = (props: SamePromotionProductListProps) => {
    const { product } = props;
    const promotionId = product?.promotion?.identifier;

    const { execute, result, isLoading } = useAsyncDispatcher(productFlow.getProductsForPromotion, {
        initialLoadingState: true,
    });

    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);

    const getProductsForPromotion = useCallback(() => {
        if (promotionId) {
            execute(promotionId, 50, currentProductionUnit);
        }
    }, [execute, promotionId, currentProductionUnit]);

    const otherProductsOnPromotion = result?.products?.filter(
        (item) => item.identifier !== product?.identifier,
    );

    const { onProductsLoaded } = props;

    useEffect(() => {
        if (otherProductsOnPromotion) {
            onProductsLoaded(otherProductsOnPromotion.length);
        }
    }, [onProductsLoaded, otherProductsOnPromotion]);

    const { swiperOptions, cellsInRow, gridAdditionalClasses, swiperMainClass, orientation } =
        useVerticalProductTeaserContainer('list');

    useEffect(() => {
        getProductsForPromotion();
    }, [getProductsForPromotion]);

    const headerId = useId();

    if (!promotionId) return null;

    if (!isLoading && !otherProductsOnPromotion?.length) {
        return null;
    }

    return (
        <ListBlock
            as="aside"
            aria-labelledby={headerId}
            headerSlot={<BlockHeader id={headerId}>{listHeading}</BlockHeader>}
            skipLinkOptions={{
                'aria-describedby': headerId,
            }}
            isLoading={isLoading}
            items={otherProductsOnPromotion}
            useSwiper
            swiperMainClassName={swiperMainClass}
            swiperOptions={swiperOptions}
            cellsInRow={cellsInRow}
            analyticsListName={listHeading}
            horizontalHidden={false}
            gridClasses={gridAdditionalClasses}
            itemUniqueKey={(item, index) => `${item.identifier}_${index}`}
            renderItem={(_product, index, listPosition) => (
                <BuyableProductTeaser
                    orientation={orientation}
                    product={_product}
                    analyticsOptions={{
                        list: listHeading,
                        position: index,
                        listPosition,
                    }}
                    isAsyncLink
                />
            )}
            renderSkeleton={() => null}
        />
    );
};

export default SamePromotionProductList;
